import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';

export default function Dashboard() {
  const { profil } = useAuth();
  const navigate = useNavigate();
  const isTemsilci = profil?.rol === 'temsilci';
  const isDepocu = profil?.rol === 'depocu';

  if (isTemsilci) return <TemsilciDashboard profil={profil} navigate={navigate} />;
  if (isDepocu) return <DepocuDashboard profil={profil} navigate={navigate} />;
  return <AdminDashboard profil={profil} navigate={navigate} />;
}

// FIX: Türkiye saatine (UTC+3) göre bugünün başlangıcını, UTC ANI olarak döndürür.
// created_at veritabanında UTC (timezone'suz) saklanıyor. Önceden "+03:00" ekli yerel
// string gönderiliyordu; Postgres bunu timestamp'e çevirirken offset'i ATIYOR, böylece
// sınır yanlış oluyor ve gece 00:00–03:00 arası girilen siparişler "dün" sayılıp
// bugünden düşüyordu. .toISOString() ile TR gün başlangıcının UTC karşılığını (ör.
// 00:00 TR → bir önceki gün 21:00 UTC) gönderiyoruz; bu, UTC saklanan kayıtlarla doğru eşleşir.
function bugunBaslangic() {
  const now = new Date();
  const trNow = new Date(now.toLocaleString('en-US', { timeZone: 'Europe/Istanbul' }));
  const y = trNow.getFullYear();
  const m = String(trNow.getMonth() + 1).padStart(2, '0');
  const d = String(trNow.getDate()).padStart(2, '0');
  return new Date(`${y}-${m}-${d}T00:00:00+03:00`).toISOString();
}

// ─── DEPOCU DASHBOARD ───
function DepocuDashboard({ profil, navigate }) {
  const [siparisler, setSiparisler] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ kargoBekleyen: 0, bugunKargolanan: 0 });

  useEffect(() => { fetchData(); }, []);

  async function fetchData() {
    const bugun = bugunBaslangic();
    const [bekleyen, kargolanan, son] = await Promise.all([
      supabase.from('siparisler').select('id', { count: 'exact' }).eq('siparis_durumu', 'hazirlaniyor'),
      supabase.from('siparisler').select('id', { count: 'exact' }).eq('siparis_durumu', 'kargoda').gte('updated_at', bugun),
      supabase.from('siparisler')
        .select('*, firmalar(ad)')
        .in('siparis_durumu', ['alinidi', 'hazirlaniyor'])
        .order('created_at', { ascending: false })
        .limit(20),
    ]);
    setStats({ kargoBekleyen: bekleyen.count || 0, bugunKargolanan: kargolanan.count || 0 });
    setSiparisler(son.data || []);
    setLoading(false);
  }

  const durumBadge = {
    alinidi: <span className="badge badge-blue">Alındı</span>,
    hazirlaniyor: <span className="badge badge-yellow">Hazırlanıyor</span>,
    kargoda: <span className="badge badge-orange">Kargoda</span>,
    teslim: <span className="badge badge-green">Teslim</span>,
    iade: <span className="badge badge-red">İade</span>,
    iptal: <span className="badge badge-gray">İptal</span>,
  };

  if (loading) return <div className="loading">⏳ Yükleniyor...</div>;

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Merhaba, {profil?.ad} 👋</h1>
          <p className="page-subtitle">{new Date().toLocaleDateString('tr-TR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
        </div>
      </div>

      <div className="stat-grid" style={{ gridTemplateColumns: 'repeat(2, 1fr)' }}>
        <div className="stat-card"><div className="stat-label">Kargo Bekleyen</div><div className="stat-value" style={{ color: 'var(--yellow)' }}>{stats.kargoBekleyen}</div></div>
        <div className="stat-card"><div className="stat-label">Bugün Kargolanan</div><div className="stat-value" style={{ color: 'var(--green)' }}>{stats.bugunKargolanan}</div></div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 20 }}>
        {[
          { icon: '🛒', label: 'Siparişler', to: '/siparisler' },
          { icon: '🏭', label: 'Depolar', to: '/depolar' },
          { icon: '🔍', label: 'Arama', to: '/arama' },
        ].map(item => (
          <button key={item.to} className="card" onClick={() => navigate(item.to)}
            style={{ textAlign: 'center', cursor: 'pointer', padding: '16px 12px' }}
            onMouseOver={e => e.currentTarget.style.borderColor = 'var(--accent)'}
            onMouseOut={e => e.currentTarget.style.borderColor = 'var(--border)'}>
            <div style={{ fontSize: 24, marginBottom: 6 }}>{item.icon}</div>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)' }}>{item.label}</div>
          </button>
        ))}
      </div>

      <div className="card" style={{ padding: 0 }}>
        <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)', fontWeight: 700 }}>
          Kargo Bekleyen Siparişler
        </div>
        <table className="table">
          <thead>
            <tr><th>Sipariş No</th><th>Müşteri</th><th>Şehir</th><th>Firma</th><th>Durum</th><th>Tarih</th></tr>
          </thead>
          <tbody>
            {siparisler.length === 0 ? (
              <tr><td colSpan={6} style={{ textAlign: 'center', padding: 30, color: 'var(--text2)' }}>Bekleyen sipariş yok</td></tr>
            ) : siparisler.map(s => (
              <tr key={s.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/siparisler/${s.id}`)}>
                <td><span className="mono" style={{ color: 'var(--accent)', fontWeight: 600 }}>{s.siparis_no}</span></td>
                <td>{s.musteriler?.ad || '-'}</td>
                <td style={{ color: 'var(--text2)' }}>{s.musteriler?.sehir || '-'}</td>
                <td style={{ color: 'var(--text2)' }}>{s.firmalar?.ad || '-'}</td>
                <td>{durumBadge[s.siparis_durumu]}</td>
                <td style={{ fontSize: 12, color: 'var(--text2)' }}>{new Date(s.created_at).toLocaleDateString('tr-TR')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── TEMSİLCİ DASHBOARD ───
function TemsilciDashboard({ profil, navigate }) {
  const [stats, setStats] = useState({ bugunSiparis: 0, bugunKomisyon: 0, bekleyenKomisyon: 0, odenmemisSiparis: 0, toplamMusteri: 0 });
  const [sonSiparisler, setSonSiparisler] = useState([]);
  const [gecikmisSiparisler, setGecikmisSiparisler] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchData(); }, []);

  async function fetchData() {
    const bugun = bugunBaslangic();
    const [bugunS, odenmemis, musteriler, komisyonlar, odemeler] = await Promise.all([
      supabase.from('siparisler').select('toplam_komisyon').eq('temsilci_id', profil.id).gte('created_at', bugun),
      supabase.from('siparisler').select('id', { count: 'exact' }).eq('temsilci_id', profil.id).eq('odeme_durumu', 'odenmedi'),
      supabase.from('musteriler').select('id', { count: 'exact' }).eq('temsilci_id', profil.id),
      supabase.from('siparisler').select('toplam_komisyon').eq('temsilci_id', profil.id).not('siparis_durumu', 'eq', 'iptal'),
      supabase.from('komisyon_odemeleri').select('tutar').eq('temsilci_id', profil.id),
    ]);

    const bugunKomisyon = (bugunS.data || []).reduce((a, r) => a + (r.toplam_komisyon || 0), 0);
    const toplamKomisyon = (komisyonlar.data || []).reduce((a, r) => a + (r.toplam_komisyon || 0), 0);
    const toplamOdenen = (odemeler.data || []).reduce((a, r) => a + (r.tutar || 0), 0);

    setStats({
      bugunSiparis: bugunS.data?.length || 0,
      bugunKomisyon,
      bekleyenKomisyon: toplamKomisyon - toplamOdenen,
      odenmemisSiparis: odenmemis.count || 0,
      toplamMusteri: musteriler.count || 0,
    });

    // 15 gün geçmiş ödenmemiş siparişler
    const onbesGunOnce = new Date();
    onbesGunOnce.setDate(onbesGunOnce.getDate() - 15);
    const { data: gecikmiş } = await supabase
      .from('siparisler')
      .select('id, siparis_no, toplam_tutar, created_at, musteri_id')
      .eq('temsilci_id', profil.id)
      .eq('odeme_durumu', 'odenmedi')
      .neq('siparis_tipi', 'numune')
      .not('siparis_durumu', 'eq', 'iptal')
      .lte('created_at', onbesGunOnce.toISOString())
      .order('created_at', { ascending: true });
    if (gecikmiş && gecikmiş.length > 0) {
      const mIds = [...new Set(gecikmiş.map(s => s.musteri_id).filter(Boolean))];
      const { data: mList } = await supabase.from('musteriler').select('id, ad, telefon').in('id', mIds);
      const mMap = {};
      (mList || []).forEach(m => { mMap[m.id] = m; });
      gecikmiş.forEach(s => { s.musteriler = mMap[s.musteri_id] || null; });
    }
    setGecikmisSiparisler(gecikmiş || []);

    setLoading(false);
  }

  const odemeBadge = {
    odendi: <span className="badge badge-green">Ödendi</span>,
    odenmedi: <span className="badge badge-red">Ödenmedi</span>,
    kismi: <span className="badge badge-yellow">Kısmi</span>,
  };
  const durumBadge = {
    alinidi: <span className="badge badge-blue">Alındı</span>,
    hazirlaniyor: <span className="badge badge-yellow">Hazırlanıyor</span>,
    kargoda: <span className="badge badge-orange">Kargoda</span>,
    teslim: <span className="badge badge-green">Teslim</span>,
    iade: <span className="badge badge-red">İade</span>,
    iptal: <span className="badge badge-gray">İptal</span>,
  };

  if (loading) return <div className="loading">⏳ Yükleniyor...</div>;

  const fmt = v => (v || 0).toLocaleString('tr-TR');

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Merhaba, {profil?.ad} 👋</h1>
          <p className="page-subtitle">{new Date().toLocaleDateString('tr-TR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
        </div>
        <button className="btn btn-primary" onClick={() => navigate('/siparisler/yeni')}>+ Yeni Sipariş</button>
      </div>

      <div className="stat-grid" style={{ gridTemplateColumns: 'repeat(2, 1fr)' }}>
        <div className="stat-card"><div className="stat-label">Bugün Sipariş</div><div className="stat-value">{stats.bugunSiparis}</div></div>
        <div className="stat-card"><div className="stat-label">Bugün Komisyon</div><div className="stat-value" style={{ color: 'var(--yellow)', fontSize: 16 }}>{fmt(stats.bugunKomisyon)} ₺</div></div>
        <div className="stat-card"><div className="stat-label">Bekleyen Komisyon</div><div className="stat-value" style={{ color: 'var(--red)', fontSize: 16 }}>{fmt(stats.bekleyenKomisyon)} ₺</div></div>
        <div className="stat-card"><div className="stat-label">Ödenmemiş Sipariş</div><div className="stat-value" style={{ color: 'var(--orange)' }}>{stats.odenmemisSiparis}</div></div>
        <div className="stat-card"><div className="stat-label">Toplam Müşteri</div><div className="stat-value">{stats.toplamMusteri}</div></div>
      </div>

      {gecikmisSiparisler.length > 0 && (
        <div className="card" style={{ marginBottom: 16, border: '1px solid rgba(239,68,68,0.3)', background: 'rgba(239,68,68,0.04)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <div>
              <h3 style={{ fontWeight: 700, fontSize: 15, color: 'var(--red)' }}>⚠️ Gecikmiş Ödemeler ({gecikmisSiparisler.length})</h3>
              <p style={{ fontSize: 12, color: 'var(--text2)', marginTop: 2 }}>15 günden fazla süredir ödenmemiş siparişler</p>
            </div>
            <button
              style={{ background: '#25D366', border: 'none', borderRadius: 8, color: '#fff', cursor: 'pointer', fontSize: 13, padding: '8px 16px', fontWeight: 700 }}
              onClick={() => {
                const grup = {};
                gecikmisSiparisler.forEach(s => {
                  const mid = s.musteri_id;
                  if (!grup[mid]) grup[mid] = { musteri: s.musteriler, siparisler: [] };
                  grup[mid].siparisler.push(s);
                });
                Object.values(grup).forEach((g, i) => {
                  setTimeout(() => {
                    const { musteri, siparisler: sList } = g;
                    const tel = (musteri?.telefon || '').replace(/\D/g, '').replace(/^0/, '');
                    const toplam = sList.reduce((a, s) => a + (s.toplam_tutar || 0), 0);
                    const satirlar = sList.map(s => {
                      const urunler = (s.siparis_kalemleri || []).map(k => `   ${k.urunler?.ad} x${k.miktar}`).join('\n');
                      const tarih = new Date(new Date(s.created_at).getTime() + 3 * 60 * 60 * 1000).toLocaleDateString('tr-TR');
                      const gun = Math.floor((Date.now() - new Date(s.created_at).getTime()) / 86400000);
                      return `📦 Sipariş #${s.siparis_no} - ${tarih} (${gun} gün)\n${urunler}\n   → ${(s.toplam_tutar || 0).toLocaleString('tr-TR')} ₺`;
                    }).join('\n\n');
                    const mesaj = `Merhaba ${musteri?.ad || ''},\n\n${sList.length === 1 ? 'Aşağıdaki siparişinize ait ödemeyi muhasebe kayıtlarımızda bulamadık.' : 'Aşağıdaki siparişlerinize ait ödemeyi muhasebe kayıtlarımızda bulamadık.'}\n\n${satirlar}\n\n💰 Toplam Tutar: ${toplam.toLocaleString('tr-TR')} ₺\n\nEğer ödemenizi gerçekleştirdiyseniz, dekontunuzu bu mesaja yanıt olarak iletmenizi rica ederiz.\nHenüz ödeme yapılmadıysa, hatırlatmak istedik.\n\nİlginiz için teşekkür eder, iyi günler dileriz. 🙏`;
                    const waUrl = tel ? `https://wa.me/90${tel}?text=${encodeURIComponent(mesaj)}` : `https://wa.me/?text=${encodeURIComponent(mesaj)}`;
                    window.open(waUrl, '_blank');
                  }, i * 1000);
                });
              }}>
              📱 Ödeme Takibi Gönder
            </button>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {gecikmisSiparisler.map(s => {
              const gun = Math.floor((Date.now() - new Date(s.created_at).getTime()) / 86400000);
              return (
                <div key={s.id} onClick={() => navigate(`/siparisler/${s.id}`)}
                  style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', background: 'var(--bg)', borderRadius: 'var(--r)', border: '1px solid var(--border)', cursor: 'pointer' }}>
                  <div>
                    <span className="mono" style={{ color: 'var(--accent)', fontWeight: 700, fontSize: 13 }}>{s.siparis_no}</span>
                    <span style={{ marginLeft: 8, fontSize: 13 }}>{s.musteriler?.ad || '-'}</span>
                    <span style={{ marginLeft: 8, fontSize: 11, color: 'var(--red)', fontWeight: 600 }}>{gun} gün gecikmiş</span>
                    {s.temsilci_ad && s.temsilci_ad !== '-' && (
                      <span style={{ marginLeft: 8, fontSize: 11, color: 'var(--text2)' }}>• {s.temsilci_ad}</span>
                    )}
                  </div>
                  <span style={{ fontWeight: 700, color: 'var(--red)' }}>{(s.toplam_tutar || 0).toLocaleString('tr-TR')} ₺</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

    </div>
  );
}

// ─── ADMİN DASHBOARD ───
function AdminDashboard({ profil, navigate }) {
  const [stats, setStats] = useState({ bugunSiparis: 0, bugunGelir: 0, odenmemis: 0, kargoBekleyen: 0, toplamMusteri: 0 });
  const [sonSiparisler, setSonSiparisler] = useState([]);
  const [gecikmisSiparisler, setGecikmisSiparisler] = useState([]);
  const [etkenUyarilari, setEtkenUyarilari] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
    // Etken madde uyarisi yonetim icindir; temsilci ve depocuda calistirilmaz (performans)
    if (profil?.rol !== 'temsilci' && profil?.rol !== 'depocu') fetchEtkenUyarilari();
  }, [profil]);

  async function fetchEtkenUyarilari() {
    const { data: emler } = await supabase.from('etken_maddeler')
      .select('*, urun_etken_maddeler(miktar_ml, urun_id)').eq('aktif', true);
    if (!emler || emler.length === 0) return;

    const tumUrunIds = [...new Set(emler.flatMap(em => (em.urun_etken_maddeler || []).map(u => u.urun_id)))];
    if (tumUrunIds.length === 0) return;

    // Iptal edilenler haric, 1000 satir limitini asmak icin sayfali
    const satis = {};
    const SAYFA = 1000;
    for (let i = 0; i < 100; i++) {
      const { data, error } = await supabase.from('siparis_kalemleri')
        .select('miktar, urun_id, siparisler!inner(siparis_durumu)')
        .in('urun_id', tumUrunIds)
        .neq('siparisler.siparis_durumu', 'iptal')
        .range(i * SAYFA, (i + 1) * SAYFA - 1);
      if (error) { console.error('etken madde kalem hatasi', error); break; }
      (data || []).forEach(k => { satis[k.urun_id] = (satis[k.urun_id] || 0) + (k.miktar || 0); });
      if (!data || data.length < SAYFA) break;
    }

    // Temel birim mg (veya g) -> kg
    const kgCevir = (m, birim) => (birim === 'g' ? (Number(m) || 0) / 1000 : (Number(m) || 0) / 1000000);

    const uyarilar = [];
    for (const em of emler) {
      let toplam = 0;
      (em.urun_etken_maddeler || []).forEach(uem => {
        toplam += (satis[uem.urun_id] || 0) * (Number(uem.miktar_ml) || 0);
      });
      if (!em.uyari_1) continue;
      if (toplam >= Number(em.uyari_1)) {
        const seviye = em.uyari_2 && toplam >= Number(em.uyari_2) ? 2 : 1;
        uyarilar.push({
          em, toplam, seviye,
          toplamKG: kgCevir(toplam, em.birim),
          esikKG: kgCevir(seviye === 2 ? em.uyari_2 : em.uyari_1, em.birim),
        });
      }
    }
    setEtkenUyarilari(uyarilar);
  }

  async function fetchData() {
    const bugun = bugunBaslangic();

    const [bugunSiparisler, odenmemis, kargo, musteriler] = await Promise.all([
      // FIX: count: 'exact' ile hem sayı hem veri alınıyor
      supabase.from('siparisler')
        .select('toplam_tutar', { count: 'exact' })
        .gte('created_at', bugun)
        .not('siparis_durumu', 'eq', 'iptal'),
      supabase.from('siparisler')
        .select('id', { count: 'exact' })
        .eq('odeme_durumu', 'odenmedi')
        .not('siparis_durumu', 'eq', 'iptal'),
      supabase.from('siparisler')
        .select('id', { count: 'exact' })
        .eq('siparis_durumu', 'hazirlaniyor'),
      supabase.from('musteriler')
        .select('id', { count: 'exact' })
        .eq('aktif', true),
    ]);

    const bugunGelir = (bugunSiparisler.data || []).reduce((s, r) => s + (r.toplam_tutar || 0), 0);

    // Son 8 sipariş — filtre yok, tüm zamanlar
    const { data: son } = await supabase
      .from('siparisler')
      .select('*, firmalar(ad)')
      .order('created_at', { ascending: false })
      .limit(8);
    // Müşteri adlarını ayrı çek
    if (son && son.length > 0) {
      const mIds = [...new Set(son.map(s => s.musteri_id).filter(Boolean))];
      if (mIds.length > 0) {
        const { data: mList } = await supabase.from('musteriler').select('id, ad').in('id', mIds);
        const mMap = {};
        (mList || []).forEach(m => { mMap[m.id] = m; });
        son.forEach(s => { s.musteriler = mMap[s.musteri_id] || null; });
      }
    }

    setStats({
      bugunSiparis: bugunSiparisler.count || 0,
      bugunGelir,
      odenmemis: odenmemis.count || 0,
      kargoBekleyen: kargo.count || 0,
      toplamMusteri: musteriler.count || 0,
    });
    setSonSiparisler(son || []);

    // 15 gün geçmiş ödenmemiş siparişler
    const onbesGunOnce = new Date();
    onbesGunOnce.setDate(onbesGunOnce.getDate() - 15);
    const { data: gecikmiş } = await supabase
      .from('siparisler')
      .select('id, siparis_no, toplam_tutar, created_at, musteri_id, temsilci_id, siparis_kalemleri(miktar, urunler(ad))')
      .eq('odeme_durumu', 'odenmedi')
      .neq('siparis_tipi', 'numune')
      .not('siparis_durumu', 'eq', 'iptal')
      .lte('created_at', onbesGunOnce.toISOString())
      .order('created_at', { ascending: true });
    if (gecikmiş && gecikmiş.length > 0) {
      const mIds = [...new Set(gecikmiş.map(s => s.musteri_id).filter(Boolean))];
      const tIds = [...new Set(gecikmiş.map(s => s.temsilci_id).filter(Boolean))];
      const [{ data: mList }, { data: tList }] = await Promise.all([
        supabase.from('musteriler').select('id, ad, telefon').in('id', mIds),
        tIds.length ? supabase.from('kullanicilar').select('id, ad, soyad').in('id', tIds) : { data: [] },
      ]);
      const mMap = {};
      (mList || []).forEach(m => { mMap[m.id] = m; });
      const tMap = {};
      (tList || []).forEach(t => { tMap[t.id] = `${t.ad} ${t.soyad}`; });
      gecikmiş.forEach(s => {
        s.musteriler = mMap[s.musteri_id] || null;
        s.temsilci_ad = tMap[s.temsilci_id] || '-';
      });
    }
    setGecikmisSiparisler(gecikmiş || []);

    setLoading(false);
  }

  async function handleSil(e, siparisId) {
    e.stopPropagation();
    if (!window.confirm('Bu siparişi silmek istediğinizden emin misiniz?')) return;
    await supabase.from('siparis_kalemleri').delete().eq('siparis_id', siparisId);
    await supabase.from('siparisler').delete().eq('id', siparisId);
    fetchData();
  }

  const durumBadge = {
    alinidi: <span className="badge badge-blue">Alındı</span>,
    hazirlaniyor: <span className="badge badge-yellow">Hazırlanıyor</span>,
    kargoda: <span className="badge badge-orange">Kargoda</span>,
    teslim: <span className="badge badge-green">Teslim</span>,
    iade: <span className="badge badge-red">İade</span>,
    iptal: <span className="badge badge-gray">İptal</span>,
  };
  const odemeBadge = {
    odendi: <span className="badge badge-green">Ödendi</span>,
    odenmedi: <span className="badge badge-red">Ödenmedi</span>,
    kismi: <span className="badge badge-yellow">Kısmi</span>,
    vadeli: <span className="badge badge-purple">Vadeli</span>,
  };

  if (loading) return <div className="loading">⏳ Yükleniyor...</div>;

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Dashboard</h1>
          <p className="page-subtitle">{new Date().toLocaleDateString('tr-TR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
        </div>
        <button className="btn btn-primary" onClick={() => navigate('/siparisler/yeni')}>+ Yeni Sipariş</button>
      </div>

      <div className="stat-grid">
        <div className="stat-card"><div className="stat-label">Bugün Sipariş</div><div className="stat-value">{stats.bugunSiparis}</div></div>
        <div className="stat-card"><div className="stat-label">Bugün Gelir</div><div className="stat-value" style={{ color: 'var(--green)', fontSize: 18 }}>{stats.bugunGelir.toLocaleString('tr-TR')} ₺</div></div>
        <div className="stat-card"><div className="stat-label">Ödenmemiş</div><div className="stat-value" style={{ color: 'var(--red)' }}>{stats.odenmemis}</div></div>
        <div className="stat-card"><div className="stat-label">Kargo Bekleyen</div><div className="stat-value" style={{ color: 'var(--yellow)' }}>{stats.kargoBekleyen}</div></div>
        <div className="stat-card"><div className="stat-label">Toplam Müşteri</div><div className="stat-value">{stats.toplamMusteri}</div></div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 12, marginBottom: 20 }}>
        {[
          { icon: '🛒', label: 'Siparişler', to: '/siparisler' },
          { icon: '🔍', label: 'Arama', to: '/arama' },
          { icon: '👥', label: 'Müşteriler', to: '/musteriler' },
          { icon: '📦', label: 'Ürünler', to: '/urunler' },
          { icon: '🏭', label: 'Depolar', to: '/depolar' },
          { icon: '💰', label: 'Komisyonlar', to: '/komisyonlar' },
          { icon: '💳', label: 'Ödemeler', to: '/odemeler' },
          { icon: '📈', label: 'Raporlar', to: '/raporlar' },
        ].map(item => (
          <button key={item.to} className="card" onClick={() => navigate(item.to)}
            style={{ textAlign: 'center', cursor: 'pointer', padding: '16px 12px' }}
            onMouseOver={e => e.currentTarget.style.borderColor = 'var(--accent)'}
            onMouseOut={e => e.currentTarget.style.borderColor = 'var(--border)'}>
            <div style={{ fontSize: 24, marginBottom: 6 }}>{item.icon}</div>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)' }}>{item.label}</div>
          </button>
        ))}
      </div>

      {etkenUyarilari.length > 0 && (
        <div className="card" style={{ marginBottom: 16, border: `1px solid ${etkenUyarilari.some(u => u.seviye === 2) ? 'rgba(239,68,68,0.4)' : 'rgba(234,179,8,0.4)'}`, background: etkenUyarilari.some(u => u.seviye === 2) ? 'rgba(239,68,68,0.04)' : 'rgba(234,179,8,0.04)' }}>
          <h3 style={{ fontWeight: 700, fontSize: 15, marginBottom: 10, color: etkenUyarilari.some(u => u.seviye === 2) ? 'var(--red)' : 'var(--yellow)' }}>
            🧪 Etken Madde Uyarısı
          </h3>
          {etkenUyarilari.map((u, i) => (
            <div key={i} onClick={() => navigate('/etken-maddeler')}
              style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', background: 'var(--bg)', borderRadius: 'var(--r)', border: '1px solid var(--border)', marginBottom: 6 }}>
              <div>
                <span style={{ fontWeight: 700 }}>{u.em.ad}</span>
                <span style={{ marginLeft: 8, fontSize: 12, color: 'var(--text2)' }}>
                  Toplam: {u.toplamKG.toLocaleString('tr-TR', { maximumFractionDigits: 3 })} kg
                  ({(u.toplam || 0).toLocaleString('tr-TR')} {u.em.birim || 'mg'})
                </span>
              </div>
              <span style={{ fontWeight: 700, color: u.seviye === 2 ? 'var(--red)' : 'var(--yellow)' }}>
                {(u.seviye === 2 ? '🚨 ' : '⚠️ ') + u.esikKG.toLocaleString('tr-TR', { maximumFractionDigits: 3 }) + ' kg Eşiği Aşıldı'}
              </span>
            </div>
          ))}
        </div>
      )}

      {gecikmisSiparisler.length > 0 && (
        <div className="card" style={{ marginBottom: 16, border: '1px solid rgba(239,68,68,0.3)', background: 'rgba(239,68,68,0.04)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <div>
              <h3 style={{ fontWeight: 700, fontSize: 15, color: 'var(--red)' }}>⚠️ Gecikmiş Ödemeler ({gecikmisSiparisler.length})</h3>
              <p style={{ fontSize: 12, color: 'var(--text2)', marginTop: 2 }}>15 günden fazla süredir ödenmemiş siparişler</p>
            </div>
            <button
              style={{ background: '#25D366', border: 'none', borderRadius: 8, color: '#fff', cursor: 'pointer', fontSize: 13, padding: '8px 16px', fontWeight: 700 }}
              onClick={() => {
                const grup = {};
                gecikmisSiparisler.forEach(s => {
                  const mid = s.musteri_id;
                  if (!grup[mid]) grup[mid] = { musteri: s.musteriler, siparisler: [] };
                  grup[mid].siparisler.push(s);
                });
                Object.values(grup).forEach((g, i) => {
                  setTimeout(() => {
                    const { musteri, siparisler: sList } = g;
                    const tel = (musteri?.telefon || '').replace(/\D/g, '').replace(/^0/, '');
                    const toplam = sList.reduce((a, s) => a + (s.toplam_tutar || 0), 0);
                    const satirlar = sList.map(s => {
                      const urunler = (s.siparis_kalemleri || []).map(k => `   ${k.urunler?.ad} x${k.miktar}`).join('\n');
                      const tarih = new Date(new Date(s.created_at).getTime() + 3 * 60 * 60 * 1000).toLocaleDateString('tr-TR');
                      const gun = Math.floor((Date.now() - new Date(s.created_at).getTime()) / 86400000);
                      return `📦 Sipariş #${s.siparis_no} - ${tarih} (${gun} gün)\n${urunler}\n   → ${(s.toplam_tutar || 0).toLocaleString('tr-TR')} ₺`;
                    }).join('\n\n');
                    const mesaj = `Merhaba ${musteri?.ad || ''},\n\n${sList.length === 1 ? 'Aşağıdaki siparişinize ait ödemeyi muhasebe kayıtlarımızda bulamadık.' : 'Aşağıdaki siparişlerinize ait ödemeyi muhasebe kayıtlarımızda bulamadık.'}\n\n${satirlar}\n\n💰 Toplam Tutar: ${toplam.toLocaleString('tr-TR')} ₺\n\nEğer ödemenizi gerçekleştirdiyseniz, dekontunuzu bu mesaja yanıt olarak iletmenizi rica ederiz.\nHenüz ödeme yapılmadıysa, hatırlatmak istedik.\n\nİlginiz için teşekkür eder, iyi günler dileriz. 🙏`;
                    const waUrl = tel ? `https://wa.me/90${tel}?text=${encodeURIComponent(mesaj)}` : `https://wa.me/?text=${encodeURIComponent(mesaj)}`;
                    window.open(waUrl, '_blank');
                  }, i * 1000);
                });
              }}>
              📱 Ödeme Takibi Gönder
            </button>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {gecikmisSiparisler.map(s => {
              const gun = Math.floor((Date.now() - new Date(s.created_at).getTime()) / 86400000);
              return (
                <div key={s.id} onClick={() => navigate(`/siparisler/${s.id}`)}
                  style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', background: 'var(--bg)', borderRadius: 'var(--r)', border: '1px solid var(--border)', cursor: 'pointer' }}>
                  <div>
                    <span className="mono" style={{ color: 'var(--accent)', fontWeight: 700, fontSize: 13 }}>{s.siparis_no}</span>
                    <span style={{ marginLeft: 8, fontSize: 13 }}>{s.musteriler?.ad || '-'}</span>
                    <span style={{ marginLeft: 8, fontSize: 11, color: 'var(--red)', fontWeight: 600 }}>{gun} gün gecikmiş</span>
                  </div>
                  <span style={{ fontWeight: 700, color: 'var(--red)' }}>{(s.toplam_tutar || 0).toLocaleString('tr-TR')} ₺</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="card" style={{ padding: 0 }}>
        <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontWeight: 700 }}>Son Siparişler</span>
          <button className="btn btn-ghost btn-sm" onClick={() => navigate('/siparisler')}>Tümünü Gör →</button>
        </div>
        <table className="table">
          <thead>
            <tr><th>Sipariş No</th><th>Müşteri</th><th>Firma</th><th>Tutar</th><th>Durum</th><th>Ödeme</th><th>Tarih</th><th></th></tr>
          </thead>
          <tbody>
            {sonSiparisler.length === 0 ? (
              <tr><td colSpan={8} style={{ textAlign: 'center', padding: 30, color: 'var(--text2)' }}>Henüz sipariş yok</td></tr>
            ) : sonSiparisler.map(s => (
              <tr key={s.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/siparisler/${s.id}`)}>
                <td><span className="mono" style={{ color: 'var(--accent)', fontWeight: 600 }}>{s.siparis_no}</span></td>
                <td>{s.musteriler?.ad || '-'}</td>
                <td style={{ color: 'var(--text2)' }}>{s.firmalar?.ad || '-'}</td>
                <td style={{ fontWeight: 600 }}>{(s.toplam_tutar || 0).toLocaleString('tr-TR')} ₺</td>
                <td>{durumBadge[s.siparis_durumu]}</td>
                <td>{odemeBadge[s.odeme_durumu]}</td>
                <td style={{ fontSize: 12, color: 'var(--text2)' }}>{new Date(s.created_at).toLocaleDateString('tr-TR')}</td>
                <td onClick={e => e.stopPropagation()}>
                  <button className="btn btn-danger btn-sm" onClick={e => handleSil(e, s.id)}>Sil</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
